/**
 * Headless selectors + formatters for the Insights → Sales Mix tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract. The
 * web-shell chart components consume these to pivot the long-format
 * contribution-by-job-family series into one row per month (one column per
 * family) for a stacked bar, and to derive win/lose and BrandHub-share rates
 * the endpoint deliberately leaves as raw counts (a presentation aggregate
 * over dbt-defined columns, per architecture.md's metric-ownership rule).
 */

import type {
  ContributionByJobFamilyPoint,
  JobVolumePoint,
  WinLosePoint,
} from "../data/api-types";
import { shortMonthLabel } from "./insights-monthly-revenue";

export { shortMonthLabel };

/** One month, pivoted: one numeric field per job family present in the series. */
export interface ContributionByFamilyDatum {
  period: string;
  label: string;
  total: number;
  byFamily: Record<string, number>;
}

/**
 * Pivot the long-format series (one row per period per family) into one row
 * per period with every family's amount alongside it, plus the period total.
 * Families not present for a given period default to 0 (no gaps in the bar).
 */
export function pivotContributionByFamily(
  points: ContributionByJobFamilyPoint[],
): { data: ContributionByFamilyDatum[]; families: string[] } {
  const families = Array.from(new Set(points.map((p) => p.job_family))).sort();
  const byPeriod = new Map<string, ContributionByFamilyDatum>();
  for (const p of points) {
    let row = byPeriod.get(p.period);
    if (!row) {
      row = {
        period: p.period,
        label: shortMonthLabel(p.period),
        total: 0,
        byFamily: {},
      };
      byPeriod.set(p.period, row);
    }
    row.byFamily[p.job_family] = p.booked_contribution;
    row.total += p.booked_contribution;
  }
  const data = Array.from(byPeriod.values()).sort((a, b) =>
    a.period.localeCompare(b.period),
  );
  for (const row of data) {
    for (const family of families) {
      if (!(family in row.byFamily)) row.byFamily[family] = 0;
    }
  }
  return { data, families };
}

/** Human-readable job-family label — mechanical title-case of the hyphenated slug. */
export function jobFamilyLabel(family: string): string {
  return family
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface WinLoseDatum {
  period: string;
  label: string;
  quoteCount: number;
  wonCount: number;
  lostCount: number;
  /** won / (won + lost) as a percentage; null when neither exists (no decided quotes yet). */
  winRatePct: number | null;
}

export function projectWinLose(points: WinLosePoint[]): WinLoseDatum[] {
  return points.map((p) => {
    const decided = p.won_count + p.lost_count;
    return {
      period: p.period,
      label: shortMonthLabel(p.period),
      quoteCount: p.quote_count,
      wonCount: p.won_count,
      lostCount: p.lost_count,
      winRatePct: decided > 0 ? (100 * p.won_count) / decided : null,
    };
  });
}

/** Overall win rate across every month with a decided quote (won or lost). */
export function overallWinRatePct(data: WinLoseDatum[]): number {
  const won = data.reduce((s, d) => s + d.wonCount, 0);
  const lost = data.reduce((s, d) => s + d.lostCount, 0);
  const decided = won + lost;
  return decided > 0 ? (100 * won) / decided : 0;
}

export interface JobVolumeDatum {
  period: string;
  label: string;
  brandhubCount: number;
  repCount: number;
  totalCount: number;
  /** brandhub / (brandhub + rep) as a percentage; 0 when there are no jobs. */
  brandhubSharePct: number;
}

export function projectJobVolume(points: JobVolumePoint[]): JobVolumeDatum[] {
  return points.map((p) => {
    const total = p.brandhub_count + p.rep_count;
    return {
      period: p.period,
      label: shortMonthLabel(p.period),
      brandhubCount: p.brandhub_count,
      repCount: p.rep_count,
      totalCount: total,
      brandhubSharePct: total > 0 ? (100 * p.brandhub_count) / total : 0,
    };
  });
}

/** Latest month's BrandHub share — for the KPI strip. */
export function latestBrandhubSharePct(data: JobVolumeDatum[]): number {
  return data.length ? data[data.length - 1].brandhubSharePct : 0;
}

/** First month's BrandHub share — the "was X%" delta anchor. */
export function firstBrandhubSharePct(data: JobVolumeDatum[]): number {
  return data.length ? data[0].brandhubSharePct : 0;
}
